import React, { useState, useEffect } from 'react';
import CaseTypePicker from './CaseTypePicker';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage } from '../../utils/apiError';
import { toDateOnly, toDateInputValue, todayDateInputValue } from '../../utils/datetimeLocal';
import {
  ASSIGNABLE_USERS_QUERY_KEY,
  asAssignableUsers,
  fetchAssignableUsers,
} from '../../lib/assignableUsers';
import CaseFolder from './CaseFolder';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { getAuthToken } from '../../lib/authToken';

interface CaseDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'view' | 'edit' | 'create';
  caseData?: any;
}

interface CaseFormData {
  caseNumber: string;
  title: string;
  description: string;
  status: 'open' | 'pending' | 'active' | 'on_hold' | 'closed' | 'archived';
  outcome: '' | 'won' | 'lost' | 'settled' | 'dismissed' | 'withdrawn' | 'other';
  caseType: string;
  caseTypeKey: string;
  clientId: string;
  court: string;
  judge: string;
  filingDate: string;
  closingDate: string;
  estimatedValue: number;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags: string[];
  assignedTo: string;
}

const CaseDialog: React.FC<CaseDialogProps> = ({
  open,
  onClose,
  mode,
  caseData,
}) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [timeEntries, setTimeEntries] = useState<any[]>([]);
  const [billingItems, setBillingItems] = useState<any[]>([]);
  const [formData, setFormData] = useState<CaseFormData>({
    caseNumber: '',
    title: '',
    description: '',
    status: 'open',
    outcome: '',
    caseType: '',
    caseTypeKey: '',
    clientId: '',
    court: '',
    judge: '',
    filingDate: '',
    closingDate: '',
    estimatedValue: 0,
    priority: 'medium',
    tags: [],
    assignedTo: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const response = await fetch('/api/clients', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch clients');
      }
      return response.json();
    },
  });

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ASSIGNABLE_USERS_QUERY_KEY,
    queryFn: fetchAssignableUsers,
  });
  const assignableUsers = asAssignableUsers(usersData);

  const { data: timeEntriesData, isLoading: timeEntriesLoading } = useQuery({
    queryKey: ['timeEntries', caseData?.id],
    queryFn: async () => {
      if (!caseData?.id) return { data: { timeEntries: [] } };
      
      const response = await fetch(`/api/time-entries?caseId=${caseData.id}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch time entries');
      }
      return response.json();
    },
    enabled: !!caseData?.id,
  });

  const { data: billingItemsData, isLoading: billingItemsLoading } = useQuery({
    queryKey: ['billingItems', caseData?.id],
    queryFn: async () => {
      if (!caseData?.id) return { data: { billingItems: [] } };
      
      const response = await fetch(`/api/case-billing-items/case/${caseData.id}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch billing items');
      }
      return response.json();
    },
    enabled: !!caseData?.id,
  });

  const { mutate: createInvoiceFromCase, isPending: isCreatingInvoice } = useMutation({
    mutationFn: async (caseId: string) => {
      const response = await fetch('/api/invoices/generate-from-case', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          caseId,
          timeEntryIds: timeEntriesData?.data?.timeEntries?.map((entry: any) => entry.id) || [],
          additionalItems: billingItems.map(item => ({
            type: 'service',
            description: item.description,
            quantity: item.hours,
            rate: item.rate,
            amount: item.amount,
            date: item.date,
            timekeeper: item.timekeeper,
          })),
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to create invoice');
      }
      return response.json();
    },
    onSuccess: (data) => {
      toast.success('Invoice created successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      onClose();
    },
    onError: (error) => {
      toast.error('Failed to create invoice');
      console.error('Invoice creation error:', error);
    },
  });

  const handleIssueInvoice = () => {
    if (caseData?.id) {
      createInvoiceFromCase(caseData.id);
    }
  };

  useEffect(() => {
    if (caseData && mode !== 'create') {
      const formDataToSet = {
        caseNumber: caseData.caseNumber || '',
        title: caseData.title || '',
        description: caseData.description || '',
        status: caseData.status || 'open',
        outcome: caseData.outcome || '',
        caseType: caseData.caseType || '',
        caseTypeKey: caseData.caseTypeKey || '',
        clientId: caseData.clientId || '',
        court: caseData.court || '',
        judge: caseData.judge || '',
        filingDate: caseData.filingDate ? caseData.filingDate.split('T')[0] : '',
        closingDate: caseData.closingDate ? caseData.closingDate.split('T')[0] : '',
        estimatedValue: caseData.estimatedValue ? parseFloat(caseData.estimatedValue) : 0,
        priority: caseData.priority || 'medium',
        tags: caseData.tags || [],
        assignedTo: caseData.assignedTo || '',
      };
      console.log('🔍 Setting form data:', formDataToSet);
      setFormData(formDataToSet);
    } else if (mode === 'create') {
      setFormData({
        caseNumber: '',
        title: '',
        description: '',
        status: 'open',
        outcome: '',
        caseType: '',
        caseTypeKey: '',
        clientId: '',
        court: '',
        judge: '',
        filingDate: '',
        closingDate: '',
        estimatedValue: 0,
        priority: 'medium',
        tags: [],
        assignedTo: '',
      });
    }
    
    // Load existing billing items when dialog opens
    if (billingItemsData?.data?.billingItems && caseData?.id) {
      const existingItems = billingItemsData.data.billingItems.map((item: any) => ({
        id: item.id,
        type: item.type,
        date: item.date,
        description: item.description,
        timekeeper: item.timekeeper,
        hours: item.hours,
        rate: item.rate,
        amount: item.amount,
        billable: item.billable,
        status: item.status,
      }));
      setBillingItems(existingItems);
    } else {
      setBillingItems([]);
    }
  }, [caseData, mode, open, billingItemsData]);

  // Debug billing items changes


  const createCaseMutation = useMutation({
    mutationFn: async (data: Partial<CaseFormData>) => {
      const createData = {
        caseNumber: data.caseNumber,
        title: data.title,
        description: data.description,
        status: data.status,
        outcome: data.outcome || null,
        caseType: data.caseType,
        caseTypeKey: data.caseTypeKey || null,
        clientId: data.clientId,
        court: data.court,
        judge: data.judge,
        filingDate: data.filingDate || null,
        closingDate: data.closingDate || null,
        estimatedValue: data.estimatedValue,
        priority: data.priority,
        tags: data.tags,
        assignedTo: data.assignedTo,
      };
      const response = await fetch('/api/cases', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(createData),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to create case.'));
      }
      return response.json();
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['billable-cases'] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      toast.success('Case created successfully');
      const cf = result?.courtForms;
      if (cf?.attached?.length) {
        toast.success(`${cf.attached.length} court form${cf.attached.length === 1 ? '' : 's'} added to the Matter Folder as drafts`);
      }
      if (cf?.missing?.length || cf?.error) {
        toast(cf.error || `Not yet in the forms library: ${cf.missing.join(', ')}`, { icon: '⚠️' });
      }
      onClose();
    },
    onError: (error) => {
      toast.error('Failed to create case');
      console.error('Create error:', error);
    },
  });

  const updateCaseMutation = useMutation({
    mutationFn: async (data: Partial<CaseFormData>) => {
      const updateData = {
        title: data.title,
        description: data.description,
        status: data.status,
        outcome: data.outcome || null,
        caseType: data.caseType,
        caseTypeKey: data.caseTypeKey || null,
        court: data.court,
        judge: data.judge,
        clientId: data.clientId || null,
        caseNumber: data.caseNumber,
        filingDate: toDateOnly(data.filingDate),
        closingDate: toDateOnly(data.closingDate),
        estimatedValue: data.estimatedValue,
        priority: data.priority,
        assignedTo: data.assignedTo,
      };
      console.log('🔍 Making case update request to:', `/api/cases/${caseData?.id}`);
      console.log('🔍 Update data:', updateData);
      console.log('🔍 Full request URL:', window.location.origin + `/api/cases/${caseData?.id}`);
      console.log('🔍 Request headers:', {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      });
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      console.log('🔍 About to make fetch request...');
      console.log('🔍 Request body:', JSON.stringify(updateData, null, 2));
      const response = await fetch(`/api/cases/${caseData?.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      console.log('🔍 Case update response status:', response.status);
      console.log('🔍 Case update response ok:', response.ok);
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to update case.'));
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billable-cases'] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      toast.success('Case updated successfully');
      onClose();
    },
    onError: (error) => {
      toast.error('Failed to update case');
      console.error('Update error:', error);
      if (error.name === 'AbortError') {
        console.error('❌ Case update request timed out after 10 seconds');
      }
    },
  });

  const createBillingItemMutation = useMutation({
    mutationFn: async (billingItemData: any) => {
      const response = await fetch('/api/case-billing-items', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(billingItemData),
      });
      
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to create billing item.'));
      }
      
      return response.json();
    },
    onError: (error) => {
      console.error('❌ Billing item creation error:', error);
    },
  });

  const updateBillingItemMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await fetch(`/api/case-billing-items/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to update billing item.'));
      }
      return response.json();
    },
    onError: (error) => {
      console.error('Billing item update error:', error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'view') {
      onClose();
      return;
    }
    
    if (!formData.title || !formData.caseNumber || !formData.clientId) {
      toast.error('Please fill in all required fields (Title, Case Number, and Client)');
      return;
    }
    
    setIsSubmitting(true);
    try {
      if (mode === 'create') {
        await createCaseMutation.mutateAsync(formData);
      } else if (mode === 'edit') {
        // Update case first
        try {
          await updateCaseMutation.mutateAsync(formData);
        } catch (error) {
          console.error('Case update failed:', error);
          // Continue with billing items even if case update fails
        }
        
        // Save billing items
        if (billingItems.length > 0 && caseData?.id) {
          const itemsToSave = billingItems;
          
          for (const item of itemsToSave) {
            const billingItemData = {
              caseId: caseData.id,
              clientId: formData.clientId,
              type: item.type,
              date: item.date,
              description: item.description,
              timekeeper: item.timekeeper,
              hours: item.hours,
              rate: item.rate,
              billable: item.billable,
              timeEntryId: item.id.startsWith('billing_') ? item.id.replace('billing_', '') : null,
            };

            try {
              const isNew =
                String(item.id).startsWith('new_')
                || String(item.id).startsWith('billing_');
              if (isNew) {
                await createBillingItemMutation.mutateAsync(billingItemData);
              } else {
                await updateBillingItemMutation.mutateAsync({
                  id: item.id,
                  data: billingItemData,
                });
              }
            } catch (error: any) {
              console.error('Failed to save billing item:', error);
            }
          }
          queryClient.invalidateQueries({ queryKey: ['billingItems', caseData.id] });
          toast.success(`Saved ${billingItems.filter(item => item.id.startsWith('new_') || item.id.startsWith('billing_')).length} billing items`);
          
          // Clear the billing items after saving
          setBillingItems([]);
        }
      }
    } catch (error) {
      console.error('Mutation failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof CaseFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImportTimeEntries = () => {
    if (!timeEntriesData?.data?.timeEntries) return;
    const importedItems = timeEntriesData.data.timeEntries.map((entry: any) => ({
      id: `billing_${entry.id}`,
      type: 'time',
      date: toDateInputValue(entry.startTime),
      description: entry.description,
      timekeeper: entry.user ? `${entry.user.firstName} ${entry.user.lastName}` : 'Unknown',
      hours: entry.duration / 3600,
      rate: entry.rate || 0,
      amount: entry.amount || 0,
      billable: entry.billable,
      status: 'pending'
    }));
    setBillingItems(prev => [...prev, ...importedItems]);
    toast.success(`Imported ${importedItems.length} time entries as billing items`);
  };

  const handleAddBillingItem = () => {
    const newItem = {
      id: `new_${Date.now()}`, // Mark as new item to be saved
      type: 'service',
      date: todayDateInputValue(),
      description: '',
      timekeeper: '',
      hours: 0,
      rate: 0,
      amount: 0,
      billable: true,
      status: 'pending'
    };
    setBillingItems(prev => [...prev, newItem]);
  };

  const handleUpdateBillingItem = (id: string, field: string, value: any) => {
    setBillingItems(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));

    // If this is an existing item (not a new one), save the update to the database
    if (!id.startsWith('new_') && !id.startsWith('billing_')) {
      updateBillingItemMutation.mutate({
        id,
        data: { [field]: value }
      });
    }
  };

  const handleRemoveBillingItem = (id: string) => {
    setBillingItems(prev => prev.filter(item => item.id !== id));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'success';
      case 'active': return 'primary';
      case 'pending': return 'warning';
      case 'on_hold': return 'info';
      case 'closed': return 'default';
      case 'archived': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={mode === 'view' && activeTab === 1 ? 'xl' : mode === 'view' ? 'lg' : 'md'}
      fullWidth
      PaperProps={{
        sx:
          mode === 'view' && activeTab === 1
            ? { height: '90vh', maxHeight: '90vh' }
            : undefined,
      }}
    >
      <DialogTitle>
        {mode === 'view' ? 'View Case' : mode === 'edit' ? 'Edit Case' : 'Create Case'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent
          sx={{
            minHeight: mode === 'view' && activeTab === 1 ? 0 : '400px',
            height: mode === 'view' && activeTab === 1 ? 'calc(90vh - 140px)' : undefined,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {mode === 'create' ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Case Number"
                  value={formData.caseNumber}
                  onChange={(e) => handleInputChange('caseNumber', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="on_hold">On Hold</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {(formData.status === 'closed' || formData.status === 'archived') && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Outcome</InputLabel>
                    <Select
                      value={formData.outcome}
                      onChange={(e) => handleInputChange('outcome', e.target.value)}
                      label="Outcome"
                    >
                      <MenuItem value="">Not set</MenuItem>
                      <MenuItem value="won">Won</MenuItem>
                      <MenuItem value="lost">Lost</MenuItem>
                      <MenuItem value="settled">Settled</MenuItem>
                      <MenuItem value="dismissed">Dismissed</MenuItem>
                      <MenuItem value="withdrawn">Withdrawn</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Client</InputLabel>
                  <Select
                    value={formData.clientId}
                    onChange={(e) => handleInputChange('clientId', e.target.value)}
                    label="Client"
                    disabled={clientsLoading}
                  >
                    <MenuItem value="">
                      <em>Select a client</em>
                    </MenuItem>
                    {clientsData?.data?.clients?.map((client: any) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.firstName} {client.lastName} ({client.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={formData.assignedTo}
                    onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                    label="Assigned To"
                    disabled={usersLoading}
                  >
                    <MenuItem value="">
                      <em>Select a user</em>
                    </MenuItem>
                    {assignableUsers.map((user: any) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CaseTypePicker
                  value={formData.caseType}
                  caseTypeKey={formData.caseTypeKey}
                  onChange={(label, key) => setFormData((prev) => ({ ...prev, caseType: label, caseTypeKey: key }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    label="Priority"
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Estimated Value"
                  type="number"
                  value={formData.estimatedValue}
                  onChange={(e) => handleInputChange('estimatedValue', parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: '$',
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Court"
                  value={formData.court}
                  onChange={(e) => handleInputChange('court', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Judge"
                  value={formData.judge}
                  onChange={(e) => handleInputChange('judge', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Filing Date"
                  type="date"
                  value={formData.filingDate}
                  onChange={(e) => handleInputChange('filingDate', e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Closing Date"
                  type="date"
                  value={formData.closingDate}
                  onChange={(e) => handleInputChange('closingDate', e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: activeTab === 1 ? '100%' : 'auto',
                minHeight: 0,
                flex: activeTab === 1 ? 1 : undefined,
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{ mb: activeTab === 1 ? 1 : 3, flexShrink: 0 }}
              >
                <Tab label="Details" />
                <Tab label="Documents & Notes" />
                <Tab label="Billing & Time Entries" />
              </Tabs>
              {activeTab === 0 && (
                mode === 'view' ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Case Number</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>{caseData?.caseNumber}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                      <Chip
                        label={caseData?.status}
                        color={getStatusColor(caseData?.status) as any}
                        size="small"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>{caseData?.title}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>{caseData?.description || 'No description'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Case Type</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>{caseData?.caseType || 'Not specified'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Priority</Typography>
                      <Chip
                        label={caseData?.priority}
                        color={getPriorityColor(caseData?.priority) as any}
                        size="small"
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Estimated Value</Typography>
                      <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                        {formatCurrency(caseData?.estimatedValue || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Court</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>{caseData?.court || 'Not specified'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Judge</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>{caseData?.judge || 'Not specified'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Filing Date</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {caseData?.filingDate ? toDateInputValue(caseData.filingDate) : 'Not specified'}
                      </Typography>
                    </Grid>
                    {caseData?.client && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Client</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {caseData.client.firstName} {caseData.client.lastName} ({caseData.client.email})
                        </Typography>
                      </Grid>
                    )}
                    {caseData?.assignedUser && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Assigned To</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {caseData.assignedUser.firstName} {caseData.assignedUser.lastName} ({caseData.assignedUser.email})
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Case Number"
                        value={formData.caseNumber}
                        onChange={(e) => handleInputChange('caseNumber', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={formData.status}
                          onChange={(e) => handleInputChange('status', e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="open">Open</MenuItem>
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="on_hold">On Hold</MenuItem>
                          <MenuItem value="closed">Closed</MenuItem>
                          <MenuItem value="archived">Archived</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {(formData.status === 'closed' || formData.status === 'archived') && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Outcome</InputLabel>
                          <Select
                            value={formData.outcome}
                            onChange={(e) => handleInputChange('outcome', e.target.value)}
                            label="Outcome"
                          >
                            <MenuItem value="">Not set</MenuItem>
                            <MenuItem value="won">Won</MenuItem>
                            <MenuItem value="lost">Lost</MenuItem>
                            <MenuItem value="settled">Settled</MenuItem>
                            <MenuItem value="dismissed">Dismissed</MenuItem>
                            <MenuItem value="withdrawn">Withdrawn</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Title"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Client</InputLabel>
                        <Select
                          value={formData.clientId}
                          onChange={(e) => handleInputChange('clientId', e.target.value)}
                          label="Client"
                          disabled={clientsLoading}
                        >
                          <MenuItem value="">
                            <em>Select a client</em>
                          </MenuItem>
                          {clientsData?.data?.clients?.map((client: any) => (
                            <MenuItem key={client.id} value={client.id}>
                              {client.firstName} {client.lastName} ({client.email})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Assigned To</InputLabel>
                        <Select
                          value={formData.assignedTo}
                          onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                          label="Assigned To"
                          disabled={usersLoading}
                        >
                          <MenuItem value="">
                            <em>Select a user</em>
                          </MenuItem>
                          {assignableUsers.map((user: any) => (
                            <MenuItem key={user.id} value={user.id}>
                              {user.firstName} {user.lastName} ({user.email})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        multiline
                        rows={3}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CaseTypePicker
                  value={formData.caseType}
                  caseTypeKey={formData.caseTypeKey}
                  onChange={(label, key) => setFormData((prev) => ({ ...prev, caseType: label, caseTypeKey: key }))}
                />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select
                          value={formData.priority}
                          onChange={(e) => handleInputChange('priority', e.target.value)}
                          label="Priority"
                        >
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                          <MenuItem value="urgent">Urgent</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Estimated Value"
                        type="number"
                        value={formData.estimatedValue}
                        onChange={(e) => handleInputChange('estimatedValue', parseFloat(e.target.value) || 0)}
                        InputProps={{
                          startAdornment: '$',
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Court"
                        value={formData.court}
                        onChange={(e) => handleInputChange('court', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Judge"
                        value={formData.judge}
                        onChange={(e) => handleInputChange('judge', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Filing Date"
                        type="date"
                        value={formData.filingDate}
                        onChange={(e) => handleInputChange('filingDate', e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Closing Date"
                        type="date"
                        value={formData.closingDate}
                        onChange={(e) => handleInputChange('closingDate', e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                  </Grid>
                )
              )}
              {activeTab === 1 && caseData && (
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <CaseFolder caseId={caseData.id} caseData={caseData} embedded />
                </Box>
              )}
              {activeTab === 2 && caseData?.id && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Billing & Time Entries</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleIssueInvoice}
                        disabled={isCreatingInvoice}
                      >
                        {isCreatingInvoice ? 'Creating Invoice...' : 'Issue Invoice'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleImportTimeEntries}
                        disabled={!timeEntriesData?.data?.timeEntries?.length}
                      >
                        Import All Time Entries
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleAddBillingItem}
                      >
                        Add Billing Item
                      </Button>
                    </Box>
                  </Box>
                  {timeEntriesData?.data?.timeEntries?.length > 0 && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Available Time Entries ({timeEntriesData.data.timeEntries.length})
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Total Billable Hours: {timeEntriesData.data.timeEntries
                          .filter((entry: any) => entry.billable)
                          .reduce((total: number, entry: any) => total + (entry.duration / 3600), 0)
                          .toFixed(2)} hours
                      </Typography>
                      <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <Typography variant="caption">Select</Typography>
                              </TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell>Timekeeper</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell>Hours</TableCell>
                              <TableCell>Rate</TableCell>
                              <TableCell>Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {timeEntriesData.data.timeEntries.map((entry: any) => {
                              const isSelected = billingItems.some(item => 
                                item.id === `billing_${entry.id}`
                              );
                              return (
                                <TableRow 
                                  key={entry.id}
                                  sx={{ 
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? 'action.selected' : 'inherit',
                                    '&:hover': { backgroundColor: 'action.hover' }
                                  }}
                                  onClick={() => {
                                    console.log('🔍 Clicked on time entry:', entry.id);
                                    console.log('🔍 Current billing items:', billingItems);
                                    console.log('🔍 Is selected:', isSelected);
                                    
                                    if (isSelected) {
                                      // Remove from billing items
                                      console.log('🔍 Removing from billing items');
                                      setBillingItems(prev => {
                                        const newItems = prev.filter(item => item.id !== `billing_${entry.id}`);
                                        console.log('🔍 New billing items after removal:', newItems);
                                        return newItems;
                                      });
                                    } else {
                                      // Add to billing items
                                      console.log('🔍 Adding to billing items');
                                      const newItem = {
                                        id: `billing_${entry.id}`,
                                        type: 'time',
                                        date: toDateInputValue(entry.startTime),
                                        description: entry.description,
                                        timekeeper: entry.user ? `${entry.user.firstName} ${entry.user.lastName}` : 'Unknown',
                                        hours: entry.duration / 3600,
                                        rate: entry.rate || 0,
                                        amount: entry.amount || 0,
                                        billable: entry.billable,
                                        status: 'pending'
                                      };
                                      console.log('🔍 New item to add:', newItem);
                                      setBillingItems(prev => {
                                        const newItems = [...prev, newItem];
                                        console.log('🔍 New billing items after addition:', newItems);
                                        return newItems;
                                      });
                                    }
                                  }}
                                >
                                  <TableCell padding="checkbox">
                                    <Box
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: isSelected ? 'primary.main' : 'grey.400',
                                        backgroundColor: isSelected ? 'primary.main' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontSize: '10px'
                                      }}
                                    >
                                      {isSelected && '✓'}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    {new Date(entry.startTime).toLocaleDateString()}
                                  </TableCell>
                                  <TableCell>
                                    {entry.user ? `${entry.user.firstName} ${entry.user.lastName}` : 'Unknown'}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {entry.description}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {(entry.duration / 3600).toFixed(2)}
                                  </TableCell>
                                  <TableCell>
                                    ${entry.rate || 0}
                                  </TableCell>
                                  <TableCell>
                                    ${entry.amount || 0}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Billing Items ({billingItems.length})
                    </Typography>
                    {billingItems.length === 0 ? (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No billing items yet. Import time entries or add manual items.
                      </Typography>
                    ) : (
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Timekeeper</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell>Hours</TableCell>
                              <TableCell>Rate</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {billingItems.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    type="date"
                                    value={item.date}
                                    onChange={(e) => handleUpdateBillingItem(item.id, 'date', e.target.value)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    value={item.timekeeper}
                                    onChange={(e) => handleUpdateBillingItem(item.id, 'timekeeper', e.target.value)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    multiline
                                    rows={2}
                                    value={item.description}
                                    onChange={(e) => handleUpdateBillingItem(item.id, 'description', e.target.value)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={item.hours}
                                    onChange={(e) => {
                                      const hours = parseFloat(e.target.value) || 0;
                                      const rate = item.rate || 0;
                                      handleUpdateBillingItem(item.id, 'hours', hours);
                                      handleUpdateBillingItem(item.id, 'amount', hours * rate);
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={item.rate}
                                    onChange={(e) => {
                                      const rate = parseFloat(e.target.value) || 0;
                                      const hours = item.hours || 0;
                                      handleUpdateBillingItem(item.id, 'rate', rate);
                                      handleUpdateBillingItem(item.id, 'amount', hours * rate);
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    ${(Number(item.amount) || 0).toFixed(2)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveBillingItem(item.id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                    {billingItems.length > 0 && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                        <Typography variant="subtitle1" color="white">
                          Total: ${billingItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0).toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            {mode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {mode !== 'view' && (
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || createCaseMutation.isPending || updateCaseMutation.isPending}
              startIcon={(isSubmitting || createCaseMutation.isPending || updateCaseMutation.isPending) ? <CircularProgress size={20} /> : null}
              onClick={() => console.log('🔍 Save button clicked')}
            >
              {(isSubmitting || createCaseMutation.isPending || updateCaseMutation.isPending) ? 'Saving...' : (mode === 'create' ? 'Create' : 'Save')}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CaseDialog;
